import { Helmet } from 'react-helmet-async';
import { site } from '@/data/content';

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  children?: React.ReactNode;
  jsonLd?: Record<string, unknown>[];
}

const defaultJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: site.name,
    url: site.baseUrl,
    logo: `${site.baseUrl}/og-image.png`,
    sameAs: [site.twitter, site.linkedin, site.youtube, site.github],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: site.name,
    url: site.baseUrl,
  },
];

export default function SEO({
  title,
  description,
  path = '',
  image = site.ogImage,
  children,
  jsonLd = [],
}: SEOProps) {
  const fullTitle = title ? `${title} | ${site.name}` : `${site.name} - ${site.tagline}`;
  const pageDescription = description || site.description;
  const url = `${site.baseUrl}${path}`;
  const ogImage = `${site.baseUrl}${image}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={url} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={site.name} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@zurihub" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />

      {children}

      {[...defaultJsonLd, ...jsonLd].map((item, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}
